import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { PassagePage } from "../components/PassagePage";
import { passageBySlug, passagePath } from "../lib/passage";
import {
  articleJsonLd,
  breadcrumbJsonLd,
  canonical,
  documentHeaders,
  faqJsonLd,
  passageOgImage,
  socialMeta,
} from "../lib/seo";

/**
 * `/verse/<ref>` — one passage.
 *
 * `BlogPosting` rather than anything cleverer. There is a `Quotation` type in
 * schema.org and it describes the verse, not the page; the page is a dated
 * piece of writing about the verse, and claiming otherwise to chase a rich
 * result is the usual way structured data becomes a lie.
 */
export const Route = createFileRoute("/verse/$slug")({
  loader: ({ params }) => {
    const passage = passageBySlug(params.slug);
    if (!passage) throw notFound();
    return passage;
  },
  headers: documentHeaders,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const path = passagePath(loaderData.slug);
    return {
      meta: [
        { title: loaderData.title },
        { name: "description", content: loaderData.description },
        ...socialMeta({
          title: loaderData.title,
          description: loaderData.description,
          path,
          lang: "en",
          image: passageOgImage(loaderData.slug),
          imageAlt: loaderData.h1,
        }),
        {
          "script:ld+json": articleJsonLd({
            headline: loaderData.h1,
            description: loaderData.description,
            path,
            published: loaderData.published,
            updated: loaderData.updated,
          }),
        },
        {
          "script:ld+json": breadcrumbJsonLd([
            { name: "Bible Shield", path: "/" },
            { name: "Passages", path: "/verse" },
            { name: loaderData.reference, path },
          ]),
        },
        ...(loaderData.faq
          ? [{ "script:ld+json": faqJsonLd(loaderData.faq) }]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical(path) }],
    };
  },
  component: VersePage,
});

function VersePage() {
  const passage = Route.useLoaderData();
  return (
    <PageChrome>
      <nav
        aria-label="breadcrumb"
        className="mx-auto mb-6 w-full max-w-[68ch] text-sm text-ink2"
      >
        <Link
          to="/verse"
          className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
        >
          passages
        </Link>
      </nav>
      <PassagePage passage={passage} />
    </PageChrome>
  );
}
